const { getElement, waitForConsentSettingsAvailability } = window.BAO.Utils

class PushCartTagalysRecommendations extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    const aside = this.externalEls['drawer-aside']?.element
    if (aside) {
      aside.setAttribute('aria-hidden', 'true')
    }
  }

  setupListeners () {
    super.setupListeners()

    this.fetchTagalysRecommendations = this.fetchTagalysRecommendations.bind(
      this,
    )
    this.requestRecommendations = this.requestRecommendations.bind(this)

    // Recommendations sit inside the cart drawer, so don't hit Tagalys (and
    // render a section per card) until the drawer is actually opened.
    this.drawer = this.closest('site-drawer')
    this.drawerOpened = !this.drawer
    this.hasPendingRequest = false
    this.inFlightKey = null
    this.drawer?.addEventListener('site-drawer:opened', e => {
      if (e.target !== this.drawer) return

      this.drawerOpened = true

      if (this.hasPendingRequest) {
        this.hasPendingRequest = false
        this.requestRecommendations()
      }
    })

    this.setupTagalysRecs()

    window.addEventListener('resize', () => {
      this.visibility = this.isVisible()
    })

    this.onCartChanged = this.onCartChanged.bind(this)
    this.onAfterRender = this.onAfterRender.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onCartChanged,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
      this.onCartChanged,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      this.onAfterRender,
    )
  }

  setupTagalysRecs () {
    const ready = !!(window?.onTagalysReady && this.widgetId && this.visibility)
    if (ready) {
      // eslint-disable-next-line no-undef
      onTagalysReady(() => {
        waitForConsentSettingsAvailability(this.requestRecommendations)
      })
    }
  }

  /**
   * Fetch now if the cart drawer has been opened, otherwise wait for it.
   * Opening the drawer also re-renders the cart, so ignore a repeat request
   * for the same cart while one is already in flight.
   *
   * @returns {void}
   */
  requestRecommendations () {
    // There's a copy per breakpoint; only the visible one should fetch.
    if (!this.checkVisibility()) return

    if (!this.drawerOpened) {
      this.hasPendingRequest = true
      return
    }

    const key = this.dataset.productId || ''
    if (this.inFlightKey === key) return

    this.inFlightKey = key
    this.fetchTagalysRecommendations().finally(() => {
      this.inFlightKey = null
    })
  }

  /**
   * On cart add/remove: hide immediately when cart is emptied; otherwise rely on AFTER_RENDER to update and show.
   * @param {CustomEvent} e
   *
   * @returns {void}
   */
  onCartChanged (e) {
    const state = e?.detail?.state

    if (this.dataset.type === 'SmartWidget') return

    if (!state?.items?.length) {
      window.tagalysCartRecs = null
      this.setSectionVisibility(false)
    }
  }

  /**
   * After cart sections have been re-rendered, sync with DOM cart state and fetch/show recommendations.
   * Runs after push-cart has updated the DOM (including data-cart-state in the footer).
   * @returns {void}
   */
  onAfterRender () {
    if (this.dataset.type === 'SmartWidget') return

    const cartStateEl = document.querySelector('[data-cart-state]')
    if (!cartStateEl?.innerHTML) return

    let state
    try {
      state = JSON.parse(cartStateEl.innerHTML)
    } catch {
      return
    }

    const items = state?.items ?? []
    const productIds = items.map(item => item.product_id).filter(Boolean)
    const productIdsStr = productIds.join(',')

    if (productIds.length === 0) {
      window.tagalysCartRecs = null
      this.setSectionVisibility(false)
      return
    }

    window.tagalysCartRecs = null
    this.setSectionVisibility(false)
    this.dataset.productId = productIdsStr

    waitForConsentSettingsAvailability(() => {
      if (typeof window?.onTagalysReady === 'function') {
        // eslint-disable-next-line no-undef
        onTagalysReady(this.requestRecommendations)
      } else {
        this.requestRecommendations()
      }
    })
  }

  async fetchTagalysRecommendations () {
    if (window.tagalysCartRecs?.length > 0) {
      const cards = await this.createProductCards(window.tagalysCartRecs)
      const hasCards =
        typeof cards === 'string' ? cards.trim().length > 0 : false
      this.renderCarousel(cards)
      this.setSectionVisibility(hasCards)
    } else {
      try {
        const response = await this.getTagalysRecommendations()
        const productCount = response?.products?.length ?? 0

        if (productCount > 0) {
          window.tagalysCartRecs = response.products
          const cards = await this.createProductCards(response.products)
          const hasCards =
            typeof cards === 'string' ? cards.trim().length > 0 : false
          this.renderCarousel(cards)
          this.setSectionVisibility(hasCards)
        } else {
          this.setSectionVisibility(false)
        }
      } catch (error) {
        this.handleError(error)
        this.setSectionVisibility(false)
      }
    }
  }

  /**
   * Open or close the recommendations drawer (right-to-left open, slide-right close).
   * Only open once products are loaded; close when empty.
   * Sets aria-hidden on this and on the drawer Aside (found via data-push-cart-tagalys-recommendations-external-el).
   * @param {boolean} open - true = drawer open (products loaded), false = drawer closed (slide right)
   */
  setSectionVisibility (open) {
    const aside = this.externalEls['drawer-aside']?.element
    if (aside) {
      aside.setAttribute('aria-hidden', open ? 'false' : 'true')
    }
  }

  getTagalysRecommendations () {
    const productIds = (this.dataset.productId || '').split(',').filter(Boolean)
    return new Promise((resolve, reject) => {
      window.Tagalys.APIConnector.Recommendations.call({
        params: {
          recommendationId: this.widgetId,
          count: this.limit,
          productIds,
        },
        onSuccess: response => resolve(response),
        onFailure: error => reject(error),
      })
    })
  }

  async createProductCards (products) {
    const cards = await Promise.all(
      products.map(async product => {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`

        try {
          const res = await window.BAO.sectionRenderer.fetchSingle(
            'push-cart-recommendations-item',
            productUrl,
          )
          const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
          const article = newDOM.querySelector('li')

          return article ? `${article.outerHTML}` : ''
        } catch (error) {
          this.handleError(error)
          return ''
        }
      }),
    )

    return cards.join('')
  }

  renderCarousel (cards) {
    const targetEl = getElement('.psh-Recommendations_Body', { context: this })
    if (!targetEl) return

    targetEl.innerHTML = `
      <ul class="psh-Recommendations_List">
        ${cards}
      </ul>`
  }

  handleError (error) {
    console.error('Error:', error)
  }

  get limit () {
    return this.dataset.limit || 8
  }

  get widgetId () {
    return this.dataset.widgetId || ''
  }

  /**
   * Element.checkVisibility() only landed in Safari 17.4 and Firefox 125, and
   * calling it on anything older threw from inside setupListeners() - which
   * took setupTagalysRecs() down with it, so those customers never got
   * recommendations at all. Fall back to a rendered-box test.
   *
   * @returns {boolean}
   */
  isVisible () {
    return typeof this.checkVisibility === 'function'
      ? this.checkVisibility()
      : !!(
          this.offsetWidth ||
          this.offsetHeight ||
          this.getClientRects().length
        )
  }

  get visibility () {
    return this._visibility || this.isVisible()
  }

  set visibility (value) {
    if (value !== this._visibility) {
      this.setupTagalysRecs()

      this._visibility = value
    }
  }
}

if (!customElements.get('push-cart-tagalys-recommendations')) {
  customElements.define(
    'push-cart-tagalys-recommendations',
    PushCartTagalysRecommendations,
  )
}
